import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Row, Col, Card,
  Container, Badge
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import useProductApi from '../../../managers/ProductManager';
import { useShoppingCart } from '../../../context/ShoppingCartContext';
import Loading, { LoadingStates } from '../../../components/Loading';
import UserAccess from '../../../auth/UserAccess';
import useUserApi, { User, UserRoles } from '../../../managers/UserManager';
import { useAuth0 } from '@auth0/auth0-react';
import { FaGlobe, FaStar } from 'react-icons/fa6';
import TagSelector from './TagSelector';
import ProductActions from './ProductActions';

interface ProductListProps {
  userId?: string | null; // Make `userId` optional and allow null
  statusTag?: string | 'PUBLIC'; // Optional prop to filter products by status tag
  showFilters?: boolean | false; // Optional prop to hide filters
  showActions?: boolean; // Optional prop to hide action buttons
}

const ProductList: React.FC<ProductListProps> = ({ userId, statusTag, showFilters = true, showActions = false }) => {
  const navigate = useNavigate();
  const [loadingState, setLoadingState] = useState<typeof LoadingStates[keyof typeof LoadingStates]>(LoadingStates.LOADING);
  const [loadingMessage, setLoadingMessage] = useState("Loading");
  const { getProducts, getUserProducts } = useProductApi();
  const { isLoading } = useAuth0();
  const { getCurrentUser } = useUserApi();
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const lastKeyRef = useRef<Record<string, any> | null>(null); // Persistent pagination key
  const isFetching = useRef(false); // Prevents multiple fetches at the same time
  const hasMoreProducts = useRef(true); // Tracks whether there are more products to load
  const observer = useRef<IntersectionObserver | null>(null); // Holds the IntersectionObserver

  // Fetch products function
  const fetchProducts = useCallback(async () => {
    if (isFetching.current || !hasMoreProducts.current) return;

    isFetching.current = true; // Prevents concurrent fetches
    setLoadingState(LoadingStates.LOADING);
    try {
      const data = userId
        ? await getUserProducts(userId, 10, lastKeyRef?.current || undefined)
        : await getProducts(statusTag, 10, lastKeyRef?.current || undefined);

      if (data.items.length === 0) {
        hasMoreProducts.current = false; // Stop further requests
      } else {
        setProducts((prevProducts) => [...prevProducts, ...data?.items]);

        if (data.lastKey) {
          lastKeyRef.current = data.lastKey; // Update pagination key persistently
        } else {
          hasMoreProducts.current = false; // Stop further fetches if no `lastKey`
        }
      }
      setLoadingState(LoadingStates.SUCCESS);
    } catch (error) {
      hasMoreProducts.current = false;
      setLoadingState(LoadingStates.ERROR);
      setLoadingMessage("Error fetching products: " + error);
      console.error("Error fetching products:", error);
    } finally {
      isFetching.current = false;
    }
  }, [lastKeyRef.current, getProducts]);

  // Effect to fetch initial products when component mounts
  useEffect(() => {
    if (products.length === 0) {
      fetchProducts(); // Fetch initial products when the component mounts
    }
  }, [fetchProducts]);

  useEffect(() => {
    if (!isLoading) {
      getCurrentUser()
        .then(setCurrentUser)
        .catch((err) => {
          console.error("Failed to fetch current user:", err);
        });
    }
  }, [isLoading]);

  const handleProductClick = (product: any) => {
    const isTemplate = product.statusTag === 'TEMPLATE';

    if (isTemplate) {
      navigate(`/products/template/${product.productId}`);
    } else {
      // Use category from product (populated from catalog), fallback to tags, then uncategorized
      const category = product?.catalogItemCategory || (product?.tags && product?.tags?.length > 0 ? product.tags[0] : "uncategorized");
      const catalogItemId = product?.catalogItemVariantId || "unknown-catalogItemId";
      navigate(`/products/${catalogItemId}/${category}/${product.productId}`);
    }
  };

  // IntersectionObserver callback for the last product
  const lastProductElementRef = useCallback((node: HTMLDivElement | null) => {
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isFetching.current && hasMoreProducts.current) {
          fetchProducts(); // Fetch more products when the last product is visible
        }
      },
      {
        root: null,
        rootMargin: "200px", // Trigger earlier for smoother loading
        threshold: 0,
      }
    );

    if (node) observer.current.observe(node);
  }, [fetchProducts]);

  if ((loadingState === LoadingStates.LOADING || loadingState === LoadingStates.ERROR) && products.length === 0) {
    return <div><Loading state={loadingState} message={loadingMessage} /></div>;
  }

  if (!products || products.length === 0) {
    return <div>No products found.</div>;
  }

  const filteredProducts = selectedTags.length === 0
    ? products
    : products.filter(product =>
      Array.isArray(product.tags) &&
      product.tags.some((tag: string) => selectedTags.includes(tag))
    );

  // Build availableTags from product data
  const availableTags = Array.from(
    new Set(products.flatMap((p) => p.tags || []))
  );

  return (
    <>
      {/* Multi-Select Tag Filter */}
      {showFilters && availableTags.length > 0 && (
        <>
          {/* xs: show collapsible */}
          <div className="d-block d-sm-none">
            <TagSelector
              selectedTags={selectedTags}
              onChange={setSelectedTags}
              availableTags={availableTags}
              collapsible={true}
            />
          </div>

          {/* sm and up: always inline */}
          <div className="d-none d-sm-block">
            <TagSelector
              selectedTags={selectedTags}
              onChange={setSelectedTags}
              availableTags={availableTags}
            />
          </div>
        </>
      )}

      <Container fluid>
        <Row className="gx-1 gy-2 gx-sm-3 gy-sm-4">
          {filteredProducts.map((product, index) => {
            const isTemplate = product.statusTag === 'TEMPLATE';

            return (
              <Col
                key={product.productId || `product-${index}`}
                xs={6}
                sm={6}
                md={4}
                lg={3}
                ref={filteredProducts.length === index + 1 ? lastProductElementRef : null}
              >
                <Card className="mb-2 border-0">
                  {/* Product image */}
                  <div
                    className="position-relative overflow-hidden bg-light d-flex align-items-center justify-content-center"
                    style={{ minHeight: '200px', aspectRatio: '1/1' }}
                  >
                    {product.previewImageUrls?.[0] ? (
                      <Card.Img
                        variant="top"
                        src={product.previewImageUrls[0]}
                        alt={product.name}
                        loading="lazy"
                        className="img-fluid ws-img-clickable rounded-top"
                        style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                        onClick={() => handleProductClick(product)}
                        onError={(e) => {
                          // Hide broken image and show placeholder
                          const img = e.target as HTMLImageElement;
                          img.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div
                        className="w-100 h-100 d-flex align-items-center justify-content-center text-muted bg-secondary rounded-top ws-img-clickable"
                        onClick={() => handleProductClick(product)}
                        style={{ cursor: 'pointer' }}
                      >
                        <span className="text-center">
                          <div>No Image</div>
                          <small>Click to view</small>
                        </span>
                      </div>
                    )}

                    {/* Template Badge */}
                    {isTemplate && (
                      <Badge
                        bg="success"
                        className="position-absolute top-0 start-0 m-2"
                        style={{ fontSize: '0.85rem', fontWeight: 'bold', zIndex: 3 }}
                      >
                        Template
                      </Badge>
                    )}

                    {/* Actions overlay (inside corner) */}
                    <div className="position-absolute top-0 end-0 p-2" style={{ zIndex: 2 }}>
                      {currentUser && (
                        <ProductActions
                          product={product}
                          currentUser={currentUser}
                          onProductsChange={(updater) => setProducts(updater(products))}
                        />
                      )}
                    </div>

                    {/* Product Title */}
                    <div className="position-absolute bottom-0 start-50 translate-middle-x" style={{ zIndex: 1 }}>
                      <div
                        className="d-flex flex-column align-items-center text-nowrap text-center bg-light text-dark px-2 py-1 rounded shadow-sm"
                        style={{
                          fontSize: '0.9rem',
                          opacity: 0.9,
                        }}
                      >
                        {/* Product Name with icons */}
                        <span className="fw-bold fst-italic">
                          <UserAccess
                            user={currentUser}
                            roles={[UserRoles.SUPER_ADMIN, UserRoles.CREATOR]}
                            operation="any"
                          >
                            {product.isFeatured
                              ? <FaStar className="me-2" title="Featured" />
                              : <FaGlobe className="me-2" title="Public" />}
                          </UserAccess>
                          {product.name || product.title}
                        </span>
                        <span className="text-muted mb-1">
                          <small>{product.catalogItemName}</small>
                        </span>
                      </div>
                    </div>
                  </div>
                </Card>
              </Col>
            );
          })}
        </Row>
      </Container>
    </>
  );
};

export default ProductList;
